'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

var mplTokenMetadata = require('@metaplex-foundation/mpl-token-metadata');
var web3_js = require('@solana/web3.js');
var Authorization = require('../Authorization.cjs');
var transferCompressedNft = require('./transferCompressedNft.cjs');
var Metadata = require('../models/Metadata.cjs');
var Operation = require('../../../types/Operation.cjs');
var Amount = require('../../../types/Amount.cjs');
var TransactionBuilder = require('../../../utils/TransactionBuilder.cjs');

const TOKEN_AUTH_RULES_ID = new web3_js.PublicKey('auth9SigNpDKz4sJJ1DfCTuZrZNSAgh9sFD3rboVmgg');

// -----------------
// Operation
// -----------------

const Key = 'TransferNftOperation';

/**
 * Transfers an NFT or SFT from one account to another.
 *
 * ```ts
 * await metaplex.nfts().transfer({
 *   nftOrSft,
 *   toOwner,
 *   amount: token(5),
 * });
 * ```
 *
 * @group Operations
 * @category Constructors
 */
const transferNftOperation = Operation.useOperation(Key);

/**
 * @group Operations
 * @category Types
 */

/**
 * @group Operations
 * @category Handlers
 */
const transferNftOperationHandler = {
  handle: async (operation, metaplex, scope) => {
    // handle the case of transferring compressed nfts
    if (!!operation.input.compression ||
    // @ts-ignore
    !!operation.input.nftOrSft?.compression?.compressed) {
      operation.input = await transferCompressedNft.prepareTransferCompressedNftBuilder(metaplex, operation.input);
      return transferCompressedNft.transferCompressedNftBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
    }
    return transferNftBuilder(metaplex, operation.input, scope).sendAndConfirm(metaplex, scope.confirmOptions);
  }
};

// -----------------
// Builder
// -----------------

/**
 * @group Transaction Builders
 * @category Inputs
 */

/**
 * Transfers an NFT or SFT from one account to another.
 *
 * ```ts
 * const transactionBuilder = metaplex
 *   .nfts()
 *   .builders()
 *   .transfer({
 *     nftOrSft,
 *     toOwner,
 *     amount: token(5),
 *   });
 * ```
 *
 * @group Transaction Builders
 * @category Constructors
 */
const transferNftBuilder = (metaplex, params, options = {}) => {
  const {
    programs,
    payer = metaplex.rpc().getDefaultFeePayer()
  } = options;
  const {
    nftOrSft,
    authority = metaplex.identity(),
    toOwner,
    amount = Amount.token(1),
    authorizationDetails
  } = params;

  // From owner.
  const fromOwner = params.fromOwner ?? Authorization.getSignerFromTokenMetadataAuthority(authority).publicKey;

  // Programs.
  const tokenMetadataProgram = metaplex.programs().getTokenMetadata(programs);
  const ataProgram = metaplex.programs().getAssociatedToken(programs);
  const tokenProgram = metaplex.programs().getToken(programs);
  const systemProgram = metaplex.programs().getSystem(programs);

  // PDAs.
  const metadata = metaplex.nfts().pdas().metadata({
    mint: nftOrSft.address,
    programs
  });
  const edition = metaplex.nfts().pdas().masterEdition({
    mint: nftOrSft.address,
    programs
  });
  const fromToken = params.fromToken ?? metaplex.tokens().pdas().associatedTokenAccount({
    mint: nftOrSft.address,
    owner: fromOwner,
    programs
  });
  const toToken = params.toToken ?? metaplex.tokens().pdas().associatedTokenAccount({
    mint: nftOrSft.address,
    owner: toOwner,
    programs
  });
  const ownerTokenRecord = metaplex.nfts().pdas().tokenRecord({
    mint: nftOrSft.address,
    token: fromToken,
    programs
  });
  const destinationTokenRecord = metaplex.nfts().pdas().tokenRecord({
    mint: nftOrSft.address,
    token: toToken,
    programs
  });

  // Auth.
  const auth = Authorization.parseTokenMetadataAuthorization(metaplex, {
    mint: nftOrSft.address,
    authority: '__kind' in authority ? authority : {
      __kind: 'holder',
      owner: authority,
      token: fromToken
    },
    authorizationDetails,
    programs
  });
  return TransactionBuilder.TransactionBuilder.make().setFeePayer(payer)

  // Update the metadata account.
  .add({
    instruction: mplTokenMetadata.createTransferInstruction({
      token: fromToken,
      tokenOwner: fromOwner,
      destination: toToken,
      destinationOwner: toOwner,
      mint: nftOrSft.address,
      metadata,
      edition: Metadata.isNonFungible(nftOrSft) ? edition : undefined,
      ownerTokenRecord: Metadata.isProgrammable(nftOrSft) ? ownerTokenRecord : undefined,
      destinationTokenRecord: Metadata.isProgrammable(nftOrSft) ? destinationTokenRecord : undefined,
      authority: auth.accounts.authority,
      payer: payer.publicKey,
      systemProgram: systemProgram.address,
      sysvarInstructions: web3_js.SYSVAR_INSTRUCTIONS_PUBKEY,
      splTokenProgram: tokenProgram.address,
      splAtaProgram: ataProgram.address,
      authorizationRules: auth.accounts.authorizationRules,
      authorizationRulesProgram: TOKEN_AUTH_RULES_ID
    }, {
      transferArgs: {
        __kind: 'V1',
        amount: amount.basisPoints,
        ...auth.data
      }
    }, tokenMetadataProgram.address),
    signers: [payer, ...auth.signers],
    key: params.instructionKey ?? 'transferNft'
  });
};

exports.transferNftBuilder = transferNftBuilder;
exports.transferNftOperation = transferNftOperation;
exports.transferNftOperationHandler = transferNftOperationHandler;
//# sourceMappingURL=transferNft.cjs.map
